import React, { useEffect, useState } from 'react';
import TransactionApi from '../api/TransactionApi';

// Utility function to get month names by number
const getMonthName = (monthNumber) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  return months[monthNumber];
};

const ProfitReport = () => {
  const [monthlyIncomes, setMonthlyIncomes] = useState(Array(12).fill(0));
  const [monthlyExpenses, setMonthlyExpenses] = useState(Array(12).fill(0));
  const [monthlyProfits, setMonthlyProfits] = useState(Array(12).fill(0));
  const [totalProfit, setTotalProfit] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]); // Store fetched transactions

  // Fetch data only once when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const res = await TransactionApi.incomes(); // Fetch data once
      setTransactions(res); // Store the transactions
      setIsLoading(false);
    };

    fetchData();
  }, []);

  // This effect will be triggered when transactions or selectedYear changes
  useEffect(() => {
    if (transactions.length > 0) {
      fetchYears(); 
      getTransactions();
    }
  }, [transactions, selectedYear]);

  const fetchYears = () => {
    const uniqueYears = [
      ...new Set(transactions.map((transaction) => new Date(transaction.paid_at).getFullYear()))
    ];
    setYears(uniqueYears);

    if (!uniqueYears.includes(new Date().getFullYear())) {
      setSelectedYear(uniqueYears[0]);
    }
  };

  const getTransactions = () => {
    const incomes = Array(12).fill(0);
    const expenses = Array(12).fill(0);

    // Process transactions
    transactions.forEach((transaction) => {
      const { amount, paid_at, type } = transaction;
      const parsedAmount = parseFloat(amount);
      const date = new Date(paid_at);
      const monthIndex = date.getMonth();
      const year = date.getFullYear();

      if (year === selectedYear) {
        if (type === 'income') {
          incomes[monthIndex] += parsedAmount;
        } else if (type === 'expense') {
          expenses[monthIndex] += parsedAmount;
        }
      }
    });

    const profits = incomes.map((income, index) => income - expenses[index]);
    const total = profits.reduce((sum, profit) => sum + profit, 0);

    setMonthlyIncomes(incomes);
    setMonthlyExpenses(expenses);
    setMonthlyProfits(profits);
    setTotalProfit(total);
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value));
  };

  return (
    <div>
      {isLoading ? (
        <div className="loading">
          <p>Loading...</p>
     
        </div>
      ) : (
      <div className="mx-auto">
        <div className="flex flex-col">
          <div className="flex justify-end mb-4">
            <select value={selectedYear} onChange={handleYearChange} className="p-2 border rounded w-20">
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-700">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400"> 
                        Type
                      </th>
                      {Array.from({ length: 12 }, (_, i) => (
                        <th
                          key={i}
                          scope="col"
                          className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400"
                        >
                          {getMonthName(i)}
                        </th>
                      ))}
                      <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400"> 
                      Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                    {/* Display total monthly incomes */}
                    <tr className="hover:bg-gray-100 dark:hover:bg-gray-700">
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                        Total Income
                      </td>
                      {monthlyIncomes.map((income, index) => (
                        <td
                          key={index}
                          className="py-4 px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {income.toFixed(2)}
                        </td>
                      ))}
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                      {monthlyIncomes.reduce((total, num) => total + num, 0).toFixed(2)}
                      </td>

                    </tr>

                    {/* Display total monthly expenses */}
                    <tr className="hover:bg-gray-100 dark:hover:bg-gray-700">
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                        Total Expense
                      </td>
                      {monthlyExpenses.map((expense, index) => (
                        <td
                          key={index}
                          className="py-4 px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {expense.toFixed(2)}
                        </td>
                      ))}
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                      {monthlyExpenses.reduce((total, num) => total + num, 0).toFixed(2)}
                      </td>
                    </tr>

                    {/* Display total monthly profits */}
                    <tr className="hover:bg-gray-100 dark:hover:bg-gray-700">
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                        Total Profit
                      </td>
                      {monthlyProfits.map((profit, index) => (
                        <td
                          key={index}
                          className="py-4 px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        >
                          {profit.toFixed(2)}
                        </td>
                      ))}
                      <td className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                      {totalProfit.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="py-4 px-6 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-white">
                  Total Yearly Profit: {totalProfit.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ProfitReport;
